import { FC } from 'react';
import { useClinicCaption } from '@views/pages/Patient/components/DetailsCard/components/ClinicCaption/hooks';
import { Controller, Form, FormProvider } from 'react-hook-form';
import { Box, Button, TextField, Typography } from '@mui/material';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { MAXIMUM_COMMENT_LENGTH } from '@common/types/constants.ts';

interface ClinicCaptionProps {
  patient_id: string;
  clinical_caption: string;
  disabled: boolean;
}

export const ClinicCaption: FC<ClinicCaptionProps> = ({
  patient_id,
  clinical_caption,
  disabled,
}) => {
  const {
    formMethods,
    onSubmit,
    isOpen,
    handleOpenCaptionField,
    handleCloseCaptionField,
    isSubmitting,
    isValid,
    isDirty,
    inputRef,
  } = useClinicCaption({ patient_id, clinical_caption });

  const { control } = formMethods;

  return (
    <Box>
      <FormProvider {...formMethods}>
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: 'fit-content',
            position: 'relative',
            gap: '1rem',
            marginTop: 16,
            marginBottom: 12,
          }}
        >
          {!isOpen ? (
            <>
              {clinical_caption.trim() ? (
                <Box
                  sx={{
                    bgcolor: theme => theme.palette.grey[100],
                    width: '100%',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10',
                  }}
                >
                  <Typography
                    sx={{
                      maxHeight: '60px',
                      boxSizing: 'border-box',
                      overflow: 'auto',
                      // wordBreak: 'break-all',
                      whiteSpace: 'pre-line',
                      flex: 1,
                    }}
                    variant='body2'
                  >
                    {clinical_caption}
                  </Typography>

                  <Button
                    variant='text'
                    onClick={handleOpenCaptionField}
                    disabled={disabled}
                  >
                    {t('general.edit')}
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    width: '100%',
                    zIndex: 5,
                  }}
                  onClick={e => {
                    if (disabled) {
                      return;
                    }
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenCaptionField();
                  }}
                >
                  <TextField
                    fullWidth
                    disabled={disabled}
                    label={t('patient.detailsCard.addClinicalCaption')}
                  />
                </Box>
              )}
            </>
          ) : (
            <Controller
              name='clinical_caption'
              control={control}
              disabled={isSubmitting}
              render={({ field, fieldState: { error } }) => (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                  }}
                >
                  <TextField
                    {...field}
                    sx={{ bgcolor: 'white' }}
                    type='text'
                    size='medium'
                    label={t('patient.detailsCard.addClinicalCaption')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={
                      error
                        ? error.message
                        : `${field.value ? field.value.length + `/${MAXIMUM_COMMENT_LENGTH}` : ''}`
                    }
                    fullWidth
                    multiline
                    autoFocus
                    disabled={disabled}
                    minRows={isOpen ? 3 : 1}
                    maxRows={isOpen ? 3 : 1}
                    onBlur={() => {
                      if (!isDirty && isOpen) {
                        handleCloseCaptionField();
                      }
                    }}
                    inputRef={inputRef}
                    slotProps={{
                      formHelperText: {
                        style: {
                          textAlign: error ? 'left' : 'right',
                          margin: 0,
                          padding: '3px 14px 0',
                        },
                      },
                    }}
                  />
                  {isOpen && (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '8px',
                        mt: 1,
                      }}
                    >
                      <Button
                        variant='text'
                        sx={{
                          fontSize: '0,875rem',
                          fontWeight: 'medium',
                          lineHeight: '24px',
                        }}
                        disabled={isSubmitting}
                        onClick={handleCloseCaptionField}
                      >
                        {t('general.cancel')}
                      </Button>
                      <Button
                        type='submit'
                        variant='contained'
                        color='secondary'
                        sx={{
                          fontSize: '0,875rem',
                          fontWeight: 'medium',
                          lineHeight: '24px',
                          color: 'white',
                        }}
                        disabled={!isValid || isSubmitting}
                      >
                        {t('general.save')}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            />
          )}
        </Form>
      </FormProvider>
    </Box>
  );
};
